/// <reference path="../../lib/tsd.d.ts" />

import {Options, ReceiverInterface} from "./receiver";
import {Collection} from "../lib/event-interface";
import {JoinedRoomModeSpecific} from "../chatserver/sx-server";
import logging from "../utils/log";

declare var Hls : any;

export class HLSReceiver implements ReceiverInterface
{
	getCurrentName(): string
	{
		return "HLSReceiver";
	}

	setFlag(s: string, enabled : boolean = false): void
	{
		this.$container[enabled ? "addClass" : "removeClass"](s);
	}

	muteForced: boolean;
	private hls: any;

	private TAG = "[Sxc.Chat.HlsReceiver]";

	private $container : JQuery;
	private video : HTMLVideoElement;

	private initialized = false;
	private ready       = false;
	private playing: boolean = false;

	private currentServer : string;
	private currentStream : string;
	private lastServer : string;
	private lastStream : string;

	private volume : number = null;

	public evt : Collection;

	constructor (private params : Options)
	{
		this.evt = new Collection("hls-receiver");
	}

	static isSupported()
	{
		return Hls.isSupported();
	}

	public connect()
	{
		// Nothing
	}

	isReady()
	{
		return this.ready;
	}

	isInitialized()
	{
		return this.initialized;
	}

	initialize($container, standalone = true)
	{
		if (this.initialized) return;
		this.initialized = true;

		this.$container = $container;

		if (standalone)
		{
			this.$container.on("mouseenter", (e) => {
				this.evt.trigger("mouseenter");
			});

			this.$container.on("mouseleave", (e) => {
				this.evt.trigger("mouseleave");
			});
		}


		this.video = document.createElement("video");
		this.$container.append(this.video);

		// Attach video event handlers
		// this is invoked on other event as well
		this.video.onplay = this._onPlaying.bind(this);
		this._onReady(null);
	}

	/**
	 * This method called when the flash object is ready.
	 */
	public _onReady(...args)
	{
		logging.log(this.TAG, "[_onReady]", args);

		$(this.video).addClass("no-letterboxing");

		if (this.volume !== null) {
			this.video.volume = this.volume / 100;
		}

		this.ready = true;
		this.evt.trigger("ready");
	}

	/**
	 * Triggered by flash when the playback starts after a play call
	 * @param args
	 * @private
	 */
	public _onPlaying(...args)
	{
		if (this.playing) return; // do not process twice
		logging.log(this.TAG, "[_onPlaying]", args);
		this.playing = true;
		this.evt.trigger("playing");
		this.$container.css("opacity", 1);
	}

	/**
	 * Triggered by flash when the stream's dimension is changed
	 */
	public _onVideoDimension(w, h)
	{
		logging.log(this.TAG, "[_onVideoDimension]", w, h);
		this.evt.trigger("videoDimension", w, h);
	}

	public _onPlayFailed(type: string, reason: string)
	{
		logging.error(this.TAG, "_onPlayFailed", type, reason);
		if (this.hls) {
			this.hls.destroy();
			this.hls = null;
		}
		this.playing = false;
		this.evt.trigger("playFailed", {
			reason: reason,
			stream: this.currentStream,
			data: {
				type: type,
				receiver: 'hls',
				streamServer: this.currentServer,
			}
		});
	}

	destroy()
	{
		if (this.hls) {
			this.hls.destroy();
			this.hls = null;
		}
		this.$container.empty();
		this.ready = false;
	}

	play(stream : string) : void
	{
		const onReady = () =>
		{
			logging.debug(this.TAG, "Starting playback", stream);

			if (this.hls) {
				this.hls.destroy();
			}

			this.playing = false;

			this.currentStream = stream;

			this.hls = new Hls({
				debug: true,
				maxBufferHole: 1,
				maxFragLookUpTolerance: 0.5,
			});

			this.hls.on(Hls.Events.ERROR, (event, data) => {
				if (data.details == 'bufferStalledError') {
					logging.error(this.TAG, "Trying to recover from bufferStalledError");
					this.hls.recoverMediaError();
					return;
				}

				if (data.fatal) {
					// try to recover if possible
					switch (data.type) {
						case Hls.ErrorTypes.NETWORK_ERROR:
							// try to recover network error
							logging.error(this.TAG, "Trying to recover from network error", data.details);
							this.hls.startLoad();
							break;
						case Hls.ErrorTypes.MEDIA_ERROR:
							logging.error(this.TAG, "Trying to recover from media error", data.details);
							this.hls.recoverMediaError();
							break;
						default:
							// cannot recover
							this._onPlayFailed(data.type, data.details);
							break;
					}
				}
				else {
					// not fatal error, log as debug
					logging.debug(this.TAG, "Playback warning", data.type, data.details);
				}
			});

			this.hls.on(Hls.Events.MEDIA_ATTACHED, () => {
				logging.debug(this.TAG, "Media attached");
				//this.hls.loadSource(this.currentStream.replace('stream-', 'hls.stream-'));
				this.hls.loadSource(this.currentStream);
			});

			this.hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
				logging.debug(this.TAG, "Manifest parsed, starting playback");
				this.video.play()
					.then(() => this._onPlaying())
					.catch((err) => this._onPlayFailed('parsed', err));
			});

			//Object.keys(Hls.Events).forEach((e) => {
			//	this.hls.on(Hls.Events[e], console.info.bind(console));
			//});

			this.hls.attachMedia(this.video);
		};

		if (!this.ready) {
			logging.log(this.TAG, "[play.queue.beforeready]", stream);
			this.evt.one("ready", onReady);
		} else {
			onReady();
		}
	}

	disconnect()
	{
		logging.log(this.TAG, "[disconnect]");
		this.stop();

		// Clear all events for the servers
		this.params.servers.forEach((server) => this.evt.off("connect." + server.id));

	}

	setVolume(volume : number):void
	{
		this.volume = volume;

		if (!this.isReady()) return;

		this.video.muted = this.muteForced || (this.volume == 0);

		this.video.volume = volume / 100;
	}

	stop():void
	{
		if (!this.isReady()) return;
		if (this.video) this.video.pause();
		this.currentStream = null;
		this.playing = false;

		if (this.hls) {
			this.hls.destroy();
			this.hls = null;
		}
	}

	restoreLastStream() : void {
		this.play(this.lastServer);
	}


	forceMute(forced : boolean) : void
	{
		this.muteForced = forced;
		this.setVolume(this.volume);
	}

	clear()
	{
		this.$container.css("opacity", 0);
	}

	reStreamIfChanged(stream : string)
	{
		if (this.currentStream && this.currentStream !== stream) {
			this.stop();
			this.play(stream);
		}
	}

	public getVideoObject() {
		return this.hls;
	}

}
